import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, errorText } from '../api';
import { ROLE_LABEL } from '../auth';
import { Card, Loading, Notice } from '../components/ui';
import { NAV } from '../components/Layout';
import type { Role } from '../types';

interface Bereich {
  schluessel: string;
  label: string;
}

interface RollenAktion {
  schluessel: string;
  faehig: boolean;
  erlaubt: boolean;
}

interface Rollenzeile {
  role: Role;
  faehigkeit: string[];
  erlaubt: string[];
  versteckt: string[];
  aktionen: RollenAktion[];
}

interface AktionSpalte {
  schluessel: string;
  label: string;
  hinweis: string;
}

interface Matrix {
  bereiche: Bereich[];
  aktionen: AktionSpalte[];
  rollen: Rollenzeile[];
}

/** Menüpunkte, die für eine Rolle grundsätzlich in Frage kommen: ein Ziel, der
 *  Bereich ist der Rolle erlaubt, und die fest hinterlegte Rollenliste passt. */
function itemsFuerRolle(role: Role, erlaubt: string[]): { to: string; label: string; gruppe: string }[] {
  const out: { to: string; label: string; gruppe: string }[] = [];
  for (const g of NAV) {
    for (const it of g.items) {
      if (!it.to) continue;
      const b = it.bereich ?? g.bereich;
      if (!erlaubt.includes(b)) continue;
      if (it.roles && !it.roles.includes(role)) continue;
      out.push({ to: it.to, label: it.label, gruppe: g.title });
    }
  }
  return out;
}

export function RolesPage() {
  const [matrix, setMatrix] = useState<Matrix | null>(null);
  const [auswahl, setAuswahl] = useState<Record<string, Set<string>>>({});
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState('');

  // Feinsteuerung Menüpunkte
  const [menuRole, setMenuRole] = useState<Role | ''>('');
  const [sichtbar, setSichtbar] = useState<Record<string, Set<string>>>({});
  const [menuBusy, setMenuBusy] = useState(false);

  const laden = useCallback(async () => {
    setError('');
    try {
      const m = await api<Matrix>('/api/rollen');
      setMatrix(m);
      const start: Record<string, Set<string>> = {};
      const sicht: Record<string, Set<string>> = {};
      for (const r of m.rollen) {
        start[r.role] = new Set(r.erlaubt);
        const anwendbar = itemsFuerRolle(r.role, r.erlaubt).map((i) => i.to);
        const versteckt = new Set(r.versteckt);
        sicht[r.role] = new Set(anwendbar.filter((to) => !versteckt.has(to)));
      }
      setAuswahl(start);
      setSichtbar(sicht);
      setMenuRole((cur) => cur || m.rollen[0]?.role || '');
    } catch (err) {
      setError(errorText(err));
    }
  }, []);

  useEffect(() => {
    void laden();
  }, [laden]);

  function umschalten(role: string, bereich: string) {
    setAuswahl((alt) => {
      const menge = new Set(alt[role]);
      if (menge.has(bereich)) menge.delete(bereich);
      else menge.add(bereich);
      return { ...alt, [role]: menge };
    });
  }

  async function speichern(role: Role) {
    setBusy(role);
    setError('');
    setMessage('');
    try {
      const m = await api<{ rollen: Rollenzeile[] }>('/api/rollen', {
        method: 'PUT',
        body: { role, bereiche: [...(auswahl[role] ?? new Set())] },
      });
      setMessage(`Rechte für „${ROLE_LABEL[role]}" gespeichert.`);
      const neu = { ...auswahl };
      for (const r of m.rollen) neu[r.role] = new Set(r.erlaubt);
      setAuswahl(neu);
      setMatrix((vorher) => (vorher ? { ...vorher, rollen: m.rollen } : vorher));
      // Sichtbarkeits-Set an die ggf. geänderten Bereiche angleichen.
      const sicht = { ...sichtbar };
      for (const r of m.rollen) {
        const anwendbar = itemsFuerRolle(r.role, r.erlaubt).map((i) => i.to);
        const versteckt = new Set(r.versteckt);
        sicht[r.role] = new Set(anwendbar.filter((to) => !versteckt.has(to)));
      }
      setSichtbar(sicht);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy('');
    }
  }

  async function aktionUmschalten(role: Role, aktion: string, erlaubt: boolean) {
    setBusy(`aktion:${role}:${aktion}`);
    setError('');
    setMessage('');
    try {
      const m = await api<{ rollen: Rollenzeile[] }>('/api/rollen/aktion', {
        method: 'PUT',
        body: { role, aktion, erlaubt },
      });
      setMatrix((vorher) => (vorher ? { ...vorher, rollen: m.rollen } : vorher));
      setMessage(`Zusatzrecht für „${ROLE_LABEL[role]}" gespeichert.`);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy('');
    }
  }

  function geaendert(r: Rollenzeile): boolean {
    const jetzt = auswahl[r.role] ?? new Set<string>();
    if (jetzt.size !== r.erlaubt.length) return true;
    return r.erlaubt.some((b) => !jetzt.has(b));
  }

  // --- Menüpunkt-Feinsteuerung ---
  const aktuelleRolle = matrix?.rollen.find((r) => r.role === menuRole);
  const menuItems = useMemo(
    () => (aktuelleRolle ? itemsFuerRolle(aktuelleRolle.role, aktuelleRolle.erlaubt) : []),
    [aktuelleRolle],
  );
  const menuGruppen = useMemo(() => {
    const map = new Map<string, { to: string; label: string }[]>();
    for (const it of menuItems) {
      if (!map.has(it.gruppe)) map.set(it.gruppe, []);
      map.get(it.gruppe)!.push({ to: it.to, label: it.label });
    }
    return [...map.entries()];
  }, [menuItems]);

  function punktUmschalten(role: string, to: string) {
    setSichtbar((alt) => {
      const menge = new Set(alt[role]);
      if (menge.has(to)) menge.delete(to);
      else menge.add(to);
      return { ...alt, [role]: menge };
    });
  }

  function menuGeaendert(): boolean {
    if (!aktuelleRolle) return false;
    const anwendbar = menuItems.map((i) => i.to);
    const sichtJetzt = sichtbar[aktuelleRolle.role] ?? new Set<string>();
    const verstecktJetzt = anwendbar.filter((to) => !sichtJetzt.has(to)).sort();
    const verstecktGespeichert = [...aktuelleRolle.versteckt].filter((to) => anwendbar.includes(to)).sort();
    return (
      verstecktJetzt.length !== verstecktGespeichert.length ||
      verstecktJetzt.some((to, i) => to !== verstecktGespeichert[i])
    );
  }

  async function menuSpeichern() {
    if (!aktuelleRolle) return;
    setMenuBusy(true);
    setError('');
    setMessage('');
    try {
      const anwendbar = menuItems.map((i) => i.to);
      const sichtJetzt = sichtbar[aktuelleRolle.role] ?? new Set<string>();
      const versteckt = anwendbar.filter((to) => !sichtJetzt.has(to));
      const m = await api<{ rollen: Rollenzeile[] }>('/api/rollen/menu', {
        method: 'PUT',
        body: { role: aktuelleRolle.role, versteckt },
      });
      setMessage(`Menüpunkte für „${ROLE_LABEL[aktuelleRolle.role]}" gespeichert.`);
      setMatrix((vorher) => (vorher ? { ...vorher, rollen: m.rollen } : vorher));
    } catch (err) {
      setError(errorText(err));
    } finally {
      setMenuBusy(false);
    }
  }

  if (!matrix) return error ? <Notice kind="error">{error}</Notice> : <Loading />;

  return (
    <>
      {error && <Notice kind="error">{error}</Notice>}
      {message && <Notice kind="success">{message}</Notice>}

      <Card title="Rollen & Rechte">
        <p className="small muted" style={{ marginTop: 0 }}>
          Zuerst grob je <strong>Bereich</strong>, darunter fein je <strong>Menüpunkt</strong>. Ein
          Bereich lässt sich einer Rolle entziehen — nie über ihre vorgesehene Fähigkeit hinaus geben
          (ausgegraute Felder). Die Bereichs­sperre gilt serverseitig.
        </p>
        <p className="small muted">
          Nicht änderbar: <strong>Administrator</strong> (Vollzugriff), <strong>Betriebsprüfung</strong>{' '}
          (nur lesend, § 147 AO) und <strong>Auftraggeber</strong> (sieht nur die eigene Ware).
        </p>
      </Card>

      <Card padded={false}>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Rolle</th>
                {matrix.bereiche.map((b) => (
                  <th key={b.schluessel} style={{ textAlign: 'center' }}>
                    {b.label}
                  </th>
                ))}
                <th />
              </tr>
            </thead>
            <tbody>
              {matrix.rollen.map((r) => (
                <tr key={r.role}>
                  <td style={{ fontWeight: 600 }}>{ROLE_LABEL[r.role]}</td>
                  {matrix.bereiche.map((b) => {
                    const kann = r.faehigkeit.includes(b.schluessel);
                    const an = auswahl[r.role]?.has(b.schluessel) ?? false;
                    return (
                      <td key={b.schluessel} style={{ textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={kann && an}
                          disabled={!kann}
                          title={kann ? '' : 'Für diese Rolle nicht vorgesehen'}
                          onChange={() => umschalten(r.role, b.schluessel)}
                        />
                      </td>
                    );
                  })}
                  <td className="num">
                    <button
                      className="small primary"
                      disabled={busy === r.role || !geaendert(r)}
                      onClick={() => speichern(r.role)}
                    >
                      {busy === r.role ? '…' : 'Speichern'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card title="Einzelne Menüpunkte je Rolle">
        <p className="small muted" style={{ marginTop: 0 }}>
          Innerhalb der erlaubten Bereiche lässt sich jeder Menüpunkt für eine Rolle abwählen. Ein
          abgewählter Punkt verschwindet aus dem Menü dieser Rolle und ist in der Oberfläche nicht mehr
          erreichbar.
        </p>
        <div className="toolbar" style={{ marginBottom: 12 }}>
          <label className="small" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            Rolle
            <select value={menuRole} onChange={(e) => setMenuRole(e.target.value as Role)}>
              {matrix.rollen.map((r) => (
                <option key={r.role} value={r.role}>
                  {ROLE_LABEL[r.role]}
                </option>
              ))}
            </select>
          </label>
          <span className="spacer" style={{ flex: 1 }} />
          <button className="small primary" disabled={menuBusy || !menuGeaendert()} onClick={() => void menuSpeichern()}>
            {menuBusy ? '…' : 'Speichern'}
          </button>
        </div>

        {menuGruppen.length === 0 ? (
          <p className="small muted">Für diese Rolle sind keine Bereiche freigegeben.</p>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
            {menuGruppen.map(([gruppe, items]) => (
              <div key={gruppe}>
                <div className="nav-group" style={{ paddingLeft: 0 }}>{gruppe}</div>
                {items.map((it) => {
                  const an = aktuelleRolle ? sichtbar[aktuelleRolle.role]?.has(it.to) ?? false : false;
                  return (
                    <label key={it.to} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0' }}>
                      <input
                        type="checkbox"
                        checked={an}
                        onChange={() => aktuelleRolle && punktUmschalten(aktuelleRolle.role, it.to)}
                      />
                      <span className="small">{it.label}</span>
                    </label>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </Card>

      {matrix.aktionen.length > 0 && (
        <Card title="Zusatzrechte">
          <p className="small muted" style={{ marginTop: 0 }}>
            Einzelne Tätigkeiten, die feiner als ein ganzer Bereich sind. Eine Änderung wirkt sofort
            (Server und Oberfläche); betroffene Benutzer sehen sie nach dem nächsten Laden. Ausgegraut
            = für diese Rolle nicht vorgesehen.
          </p>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Rolle</th>
                  {matrix.aktionen.map((a) => (
                    <th key={a.schluessel} style={{ textAlign: 'center' }} title={a.hinweis}>
                      {a.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {matrix.rollen.map((r) => (
                  <tr key={r.role}>
                    <td style={{ fontWeight: 600 }}>{ROLE_LABEL[r.role]}</td>
                    {matrix.aktionen.map((a) => {
                      const zelle = r.aktionen.find((x) => x.schluessel === a.schluessel);
                      const kann = zelle?.faehig ?? false;
                      const an = zelle?.erlaubt ?? false;
                      const arbeitet = busy === `aktion:${r.role}:${a.schluessel}`;
                      return (
                        <td key={a.schluessel} style={{ textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={kann && an}
                            disabled={!kann || arbeitet}
                            title={kann ? '' : 'Für diese Rolle nicht vorgesehen'}
                            onChange={() => void aktionUmschalten(r.role, a.schluessel, !an)}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="small muted" style={{ marginBottom: 0 }}>
            Hinweis: Der Administrator hat jedes Zusatzrecht immer. Löschen von Artikeln, Warengruppen
            und Preisauswertung bleiben den kaufmännischen Rollen vorbehalten.
          </p>
        </Card>
      )}
    </>
  );
}
